import React, { useState , useMemo} from "react";
import { IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import "../loginPage.css";
import { changePasswordApi } from "../api/changeAndResetPasswordApi";
import { toast } from "react-toastify";
import { getApiErrorMessage } from "../utils/apiError";

const PASSWORD_REGEX =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;

const ChangePassword = () => {
  const navigate = useNavigate();
  const email = localStorage.getItem("emailId");
  const isFromForgotPassword = false;
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [error, setError] = useState("");

  const isOldPasswordValid = isFromForgotPassword || oldPassword.trim().length > 0;
  const isNewPasswordValid =
    PASSWORD_REGEX.test(password) &&
    !password.includes(" ") &&
    password !== oldPassword;

  const isConfirmPasswordValid = password === confirmPassword && confirmPassword.length > 0;
  const isFormValid = useMemo(() => {
    return (
      isOldPasswordValid &&
      isNewPasswordValid &&
      isConfirmPasswordValid
    );
  }, [isOldPasswordValid, isNewPasswordValid, isConfirmPasswordValid]);

  const handleSubmit = async () => {
    if (!isFormValid) return;
    setError("");

    if (!isFromForgotPassword && !oldPassword) {
      setError("Old password is required");
      return;
    }

    if (!password) {
      setError("New password is required");
      return;
    }

    if (password.includes(" ")) {
      setError("Password must not contain spaces");
      return;
    }

    if (!PASSWORD_REGEX.test(password)) {
      setError(
        "Password must be at least 8 characters, include uppercase, lowercase, number, and special character"
      );
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      const payload = {
        email,
        oldPassword,
        newPassword: password,
      };

      const res = await changePasswordApi(payload);
      if (res?.status === true) {
        toast.success(res?.message || "Password changed successfully");

        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("role");
        localStorage.removeItem("emailId");

        setTimeout(() => {
          navigate("/");
        }, 1500);

        return;
      }    
      toast.error(res?.message || "Password change failed");
      setError(res?.message || "Password change failed");
    } catch (error) {
      const msg = getApiErrorMessage(error, "Failed to change password");
      toast.error(msg);
      setError(msg);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="login-container">
      <button
        type="button"
        className="back-button"
        onClick={handleBack}
      >
        ← Back
      </button>
      <h2>Change Password</h2>      
      {!isFromForgotPassword && (
        <div className="input-group">
          <label>Old Password</label>
          <div className="password-input-wrapper">
            <input
              type={showOldPassword ? "text" : "password"}
              placeholder="Enter old password"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
            />
            <IconButton
              onClick={() => setShowOldPassword((prev) => !prev)}
              size="small"
              disableRipple
            >
              {showOldPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </div>
        </div>
      )}

      <div className="input-group">
        <label>New Password</label>
        <div className="password-input-wrapper">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Enter new password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <IconButton
            onClick={() => setShowPassword((prev) => !prev)}
            size="small"
            disableRipple
          >
            {showPassword ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        </div>
      </div>

      <div className="input-group">
        <label>Confirm Password</label>
        <div className="password-input-wrapper">
          <input
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          <IconButton
            onClick={() => setShowConfirmPassword((prev) => !prev)}
            size="small"
            disableRipple
          >
            {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        </div>
      </div>

      {password && !PASSWORD_REGEX.test(password) && (
        <p className="error-message">
          Password must be at least 8 characters and include uppercase, lowercase, number, and special character.
        </p>
      )}

      {password && oldPassword && password === oldPassword && (
        <p className="error-message">
          New password must be different from old password.
        </p>
      )}

      {confirmPassword && password !== confirmPassword && (
        <p className="error-message">Passwords do not match.</p>
      )}

      {error && <p className="error-message">{error}</p>}

      <button
        className="login-button"
        onClick={handleSubmit}
        disabled={!isFormValid}
      >
        Submit
      </button>
    </div>
  );
};

export default ChangePassword;
